import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { callsApi } from '@/lib/api/calls.api';

import type {
  CallQuery,
  CallWithContact,
  HumanReviewData,
  PaginatedResponse,
} from '@repo/shared-types';
import type { UseQueryOptions } from '@tanstack/react-query';

export function useCalls(
  query?: Partial<CallQuery>,
  options?: Omit<UseQueryOptions<PaginatedResponse<CallWithContact>>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: ['calls', query],
    queryFn: () => callsApi.getCalls(query),
    ...options,
  });
}

export function useCall(id: string | undefined) {
  return useQuery({
    queryKey: ['calls', id],
    queryFn: () => callsApi.getCall(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'IN_PROGRESS' ||
        status === 'RINGING' ||
        status === 'QUEUED' ||
        status === 'INITIATED'
        ? 10000
        : false;
    },
  });
}

export function useDeleteCall() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => callsApi.deleteCall(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calls'] });
      queryClient.invalidateQueries({ queryKey: ['analytics'] });
    },
  });
}

export function useCallTracking(callId: string | undefined) {
  return useQuery({
    queryKey: ['calls', callId, 'tracking'],
    queryFn: () => callsApi.getCallTracking(callId!),
    enabled: !!callId,
  });
}

export function useHumanReview(callId: string | undefined) {
  return useQuery<HumanReviewData | null>({
    queryKey: ['calls', callId, 'human-review'],
    queryFn: () => callsApi.getHumanReview(callId!),
    enabled: !!callId,
  });
}

export function useUpsertHumanReview(callId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      outcome?: string | null;
      availability_data?: Record<string, unknown> | null;
      lead_data?: Record<string, unknown> | null;
    }) => callsApi.upsertHumanReview(callId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calls', callId, 'human-review'] });
      queryClient.invalidateQueries({ queryKey: ['calls', callId] });
    },
  });
}

export function useRunClassifier(callId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (opts?: { classifierConfigId?: string; name?: string }) =>
      callsApi.runClassifier(callId!, opts),
    onSuccess: () => {
      // The call detail's `classification` field comes from the view, which
      // reflects the new row immediately. Just refetch the call + runs list.
      queryClient.invalidateQueries({ queryKey: ['calls', callId] });
      queryClient.invalidateQueries({ queryKey: ['calls', callId, 'agent-runs'] });
    },
  });
}

export function useRunQuality(callId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (opts?: { qualityConfigId?: string; name?: string }) =>
      callsApi.runQuality(callId!, opts),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calls', callId] });
      queryClient.invalidateQueries({ queryKey: ['calls', callId, 'agent-runs'] });
    },
  });
}

/** Replace the boolean labels on one of a call's classification rows (ops override). */
export function useUpdateClassificationLabels(callId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ labels, name }: { labels: Record<string, boolean>; name?: string }) =>
      callsApi.updateClassificationLabels(callId!, labels, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calls', callId] });
      queryClient.invalidateQueries({ queryKey: ['calls', callId, 'agent-runs'] });
    },
  });
}

/** Replace the boolean flags on one of a call's quality rows (ops override). */
export function useUpdateQualityFlags(callId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ flags, name }: { flags: Record<string, boolean>; name?: string }) =>
      callsApi.updateQualityFlags(callId!, flags, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calls', callId] });
      queryClient.invalidateQueries({ queryKey: ['calls', callId, 'agent-runs'] });
    },
  });
}

/** Every named classification + quality run for the call. */
export function useCallAgentRuns(callId: string | undefined) {
  return useQuery({
    queryKey: ['calls', callId, 'agent-runs'],
    queryFn: () => callsApi.listAgentRuns(callId!),
    enabled: !!callId,
  });
}

export function useManualEmails(callId: string | undefined) {
  return useQuery({
    queryKey: ['calls', callId, 'manual-emails'],
    queryFn: () => callsApi.getManualEmails(callId!),
    enabled: !!callId,
  });
}

export function useSendManualEmail(callId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => callsApi.sendManualEmail(callId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calls', callId, 'manual-emails'] });
    },
  });
}

export function useWorkflowLog(callId: string | undefined) {
  return useQuery({
    queryKey: ['calls', callId, 'workflow-log'],
    queryFn: () => callsApi.getWorkflowLog(callId!),
    enabled: !!callId,
  });
}

export function useSentSms(callId: string | undefined) {
  return useQuery({
    queryKey: ['calls', callId, 'sent-sms'],
    queryFn: () => callsApi.getSentSms(callId!),
    enabled: !!callId,
  });
}

export function useUpdateHumanReviewed(campaignId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, humanReviewed }: { id: string; humanReviewed: boolean }) =>
      callsApi.updateHumanReviewed(id, humanReviewed),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calls'] });
      if (campaignId) {
        queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId] });
      }
    },
  });
}
